import { useCallback, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  ActionIcon,
  AppShell,
  Burger,
  Group,
  Image,
  NavLink,
  Text,
  Menu,
  UnstyledButton,
  Avatar,
  Box,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconFileText,
  IconFolder,
  IconSettings,
  IconLogout,
  IconUser,
  IconChevronDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconMicroscope,
  IconStethoscope,
  IconAlertCircle,
  IconSend,
  IconSearch,
  IconPackage,
  IconMessageCircle,
  IconScale,
  IconChartBar,
  IconReceipt,
  IconClipboardList,
  IconHistory,
  IconClipboardCheck,
  IconLock,
  IconEye,
  IconDashboard,
} from "@tabler/icons-react";
import { useAuthStore } from "../../store";
import { useTaskStore } from "../../store/task.store";
import {
  adminSections,
  canViewAdminMenuItem,
} from "../../data/adminNavigation";
import { isRouteAllowed } from "../../data/taskNavigation";
import { TaskSwitcher } from "./TaskSwitcher";
import { setAuthToken } from "../../lib";
import { useCurrentUser } from "../../hooks/useAuth";
import PathologistAssignmentNoticeMenu from "../cases/PathologistAssignmentNoticeMenu";
import { canManageLegacyCases } from "../../utils/legacyCaseAccess";

const GCP_LOGO_URL =
  "https://i0.wp.com/gulfcoastpath.com/wp-content/uploads/2021/02/cropped-GCP-Logo-v1.png?fit=1628%2C1071&ssl=1";

export default function AppLayout() {
  const [opened, { toggle }] = useDisclosure();
  const [desktopCollapsed, { toggle: toggleDesktop }] = useDisclosure(false);
  const { profile, logout, isAuthenticated } = useAuthStore();
  const { selectedTasks } = useTaskStore();
  useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const handleDashboardNavigation = useCallback(() => {
    navigate("/dashboard");
    if (opened) toggle();
  }, [navigate, opened, toggle]);

  const handleLegacyNavigation = useCallback(() => {
    navigate("/admin/legacy-cases");
    if (opened) toggle();
  }, [navigate, opened, toggle]);

  const handleTaskSelection = useCallback(
    () => navigate("/select-tasks"),
    [navigate]
  );

  const handlePendingWorkNavigation = useCallback(() => {
    navigate("/pending-work");
    if (opened) toggle();
  }, [navigate, opened, toggle]);

  const selectedSlugs = selectedTasks.map((t) => t.slug);
  const userPermissions = useMemo(
    () => new Set(profile?.permissions ?? []),
    [profile?.permissions]
  );
  const isAdminSelected = selectedSlugs.includes("admin");

  const canSee = (route: string) => isRouteAllowed(route, selectedSlugs);

  // Check if we're on the accessioning page or workflow detail pages
  const isAccessioning = location.pathname.startsWith("/accessioning");
  const isGrossingDetail = location.pathname.match(/^\/grossing\/[^/]+$/);
  const isEmbeddingDetail = location.pathname.match(/^\/embedding\/[^/]+$/);
  const isCuttingDetail = location.pathname.match(/^\/cutting\/[^/]+$/);
  const isPathologistReviewDetail = location.pathname.match(
    /^\/pathologist-review\/[^/]+$/
  );
  const isCaseDiscussionDetail = location.pathname.match(
    /^\/discussion-queue\/[^/]+(\/[^/]+)?$/
  );
  const hideNavbar =
    isAccessioning ||
    isGrossingDetail ||
    isEmbeddingDetail ||
    isCuttingDetail ||
    isPathologistReviewDetail ||
    isCaseDiscussionDetail;

  const handleLogout = () => {
    setAuthToken(null);
    logout();
    navigate("/login");
  };

  // Check if any admin path is active
  const isAdminActive = location.pathname.startsWith("/admin");

  // Admin search state
  const [adminSearch, setAdminSearch] = useState("");
  const [debouncedAdminSearch] = useDebouncedValue(adminSearch, 200);

  // Filter admin sections based on permissions and search
  const filteredAdminSections = useMemo(() => {
    const permissionFiltered = adminSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          canViewAdminMenuItem(item, profile)
        ),
      }))
      .filter((section) => section.items.length > 0);

    if (!debouncedAdminSearch) return permissionFiltered;
    const query = debouncedAdminSearch.toLowerCase();
    return permissionFiltered
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            section.label.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [debouncedAdminSearch, profile]);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: desktopCollapsed ? 76 : 300,
        breakpoint: "sm",
        collapsed: {
          mobile: !opened,
          desktop: hideNavbar ? !opened : false,
        },
      }}
      padding={hideNavbar ? 0 : "md"}
    >
      <AppShell.Header>
        <Group
          h="100%"
          px="md"
          justify="space-between"
          align="center"
          wrap="nowrap"
        >
          <Group wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom={hideNavbar ? undefined : "sm"}
              size="sm"
            />
            <Image
              src={GCP_LOGO_URL}
              alt="GCP"
              h={40}
              fit="contain"
              style={{ cursor: "pointer" }}
              onClick={handleLogoClick}
            />
            <Text
              size="lg"
              fw={600}
              visibleFrom="sm"
              c="gcpBlue"
              style={{ whiteSpace: "nowrap" }}
            >
              GCP LIS
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Box visibleFrom="sm">
              <TaskSwitcher />
            </Box>
            {isAuthenticated && profile && (
              <PathologistAssignmentNoticeMenu enabled />
            )}

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton aria-label="Account menu">
                  <Group gap={7} wrap="nowrap">
                    <Avatar color="gcpBlue" radius="xl" size="sm">
                      {profile?.fullName?.charAt(0) || "U"}
                    </Avatar>
                    <Text size="sm" fw={500} visibleFrom="md">
                      {profile?.fullName || "User"}
                    </Text>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  hiddenFrom="sm"
                  leftSection={<IconClipboardCheck size={14} />}
                  onClick={handleTaskSelection}
                >
                  Switch tasks
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconUser size={14} />}
                  onClick={() => navigate("/account/settings")}
                >
                  My Account
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconHistory size={14} />}
                  onClick={() => navigate("/my-activity")}
                >
                  My Activity
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item
                  leftSection={<IconLogout size={14} />}
                  color="red"
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        p={desktopCollapsed ? "xs" : "md"}
        style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <ScrollArea style={{ flex: 1 }} type="hover" scrollbarSize={6}>
          {desktopCollapsed ? (
            <ActionIcon
              variant="subtle"
              visibleFrom="sm"
              onClick={toggleDesktop}
              aria-label="Expand sidebar"
              color="gray"
              size="sm"
              mb="md"
              style={{ display: "flex", margin: "0 auto 12px" }}
            >
              <IconLayoutSidebarLeftExpand size={16} />
            </ActionIcon>
          ) : (
            <Group justify="space-between" mb="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Navigation
              </Text>
              <ActionIcon
                variant="subtle"
                visibleFrom="sm"
                onClick={toggleDesktop}
                aria-label="Collapse sidebar"
                color="gray"
                size="sm"
              >
                <IconLayoutSidebarLeftCollapse size={16} />
              </ActionIcon>
            </Group>
          )}

          {/* Dashboard — front-office task only */}
          {canSee("/dashboard") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Dashboard"}
              title="Dashboard"
              leftSection={<IconDashboard size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/dashboard"}
              onClick={handleDashboardNavigation}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Accessioning — front-office task */}
          {canSee("/accessioning") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Accessioning"}
              title="Accessioning"
              leftSection={<IconFileText size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/accessioning"}
              onClick={() => {
                navigate("/accessioning");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Cases — always visible */}
          <NavLink
            label={desktopCollapsed ? undefined : "Cases"}
            title="Cases"
            leftSection={<IconFolder size="1.2rem" stroke={1.5} />}
            active={
              location.pathname === "/cases" ||
              location.pathname.startsWith("/cases/")
            }
            onClick={() => {
              navigate("/cases");
              if (opened) toggle();
            }}
            variant="subtle"
            mb={4}
          />

          {/* Grossing Queue — grossing task */}
          {canSee("/grossing") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Grossing Queue"}
              title="Grossing Queue"
              leftSection={<IconMicroscope size="1.2rem" stroke={1.5} />}
              active={
                location.pathname === "/grossing" ||
                location.pathname.startsWith("/grossing/")
              }
              onClick={() => {
                navigate("/grossing");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Embedding Queue — embedding task */}
          {canSee("/embedding") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Embedding Queue"}
              title="Embedding Queue"
              leftSection={<IconMicroscope size="1.2rem" stroke={1.5} />}
              active={
                location.pathname === "/embedding" ||
                location.pathname.startsWith("/embedding/")
              }
              onClick={() => {
                navigate("/embedding");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Cutting Queue — cutting task */}
          {canSee("/cutting") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Cutting Queue"}
              title="Cutting Queue"
              leftSection={<IconMicroscope size="1.2rem" stroke={1.5} />}
              active={
                location.pathname === "/cutting" ||
                location.pathname.startsWith("/cutting/")
              }
              onClick={() => {
                navigate("/cutting");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Staining Queue — cutting task */}
          {canSee("/staining") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Staining Queue"}
              title="Staining Queue"
              leftSection={<IconMicroscope size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/staining"}
              onClick={() => {
                navigate("/staining");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Distribution — distribution task */}
          {canSee("/distribution") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Distribution"}
              title="Distribution"
              leftSection={<IconFileText size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/distribution"}
              onClick={() => {
                navigate("/distribution");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          <NavLink
            label={desktopCollapsed ? undefined : "Pending List"}
            title="Pending List"
            leftSection={<IconClipboardList size={20} />}
            active={location.pathname.startsWith("/pending-work")}
            onClick={handlePendingWorkNavigation}
          />

          {/* Pathologist Review — pathologist-review task */}
          {canSee("/pathologist-review") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Pathologist Review"}
              title="Pathologist Review"
              leftSection={<IconStethoscope size="1.2rem" stroke={1.5} />}
              active={location.pathname.startsWith("/pathologist-review")}
              onClick={() => {
                navigate("/pathologist-review");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Discussion Queue — pathologist-review task */}
          {canSee("/discussion-queue") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Discussion Queue"}
              title="Discussion Queue"
              leftSection={<IconMessageCircle size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/discussion-queue"}
              onClick={() => {
                navigate("/discussion-queue");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* QA Review Queue — pathologist-review task */}
          {canSee("/qa-review") && (
            <NavLink
              label={desktopCollapsed ? undefined : "QA Review Queue"}
              title="QA Review Queue"
              leftSection={<IconScale size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/qa-review"}
              onClick={() => {
                navigate("/qa-review");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Send-Outs Queue — distribution, front-office, pathologist-review tasks */}
          {canSee("/send-outs") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Send-Outs Queue"}
              title="Send-Outs Queue"
              leftSection={<IconPackage size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/send-outs"}
              onClick={() => {
                navigate("/send-outs");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Shipping Queue — distribution task */}
          {canSee("/shipping") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Shipping Queue"}
              title="Shipping Queue"
              leftSection={<IconFileText size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/shipping"}
              onClick={() => {
                navigate("/shipping");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Report Delivery — pathologist-review or admin task */}
          {canSee("/report-delivery") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Report Delivery"}
              title="Report Delivery"
              leftSection={<IconSend size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/report-delivery"}
              onClick={() => {
                navigate("/report-delivery");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Corrections Queue — front-office and pathologist-review tasks */}
          {canSee("/corrections-queue") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Corrections Queue"}
              title="Corrections Queue"
              leftSection={<IconClipboardCheck size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/corrections-queue"}
              onClick={() => {
                navigate("/corrections-queue");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Error Resolution — always visible */}
          <NavLink
            label={desktopCollapsed ? undefined : "Error Resolution"}
            title="Error Resolution"
            leftSection={<IconAlertCircle size="1.2rem" stroke={1.5} />}
            active={location.pathname === "/errors"}
            onClick={() => {
              navigate("/errors");
              if (opened) toggle();
            }}
            variant="subtle"
            mb={4}
          />

          {/* Work Lists — always visible */}
          <NavLink
            label={desktopCollapsed ? undefined : "Work Lists"}
            title="Work Lists"
            leftSection={<IconClipboardList size="1.2rem" stroke={1.5} />}
            active={location.pathname === "/work-lists"}
            onClick={() => {
              navigate("/work-lists");
              if (opened) toggle();
            }}
            variant="subtle"
            mb={4}
          />

          {/* Billing — always visible */}
          <NavLink
            label={desktopCollapsed ? undefined : "Billing"}
            title="Billing"
            leftSection={<IconReceipt size="1.2rem" stroke={1.5} />}
            active={location.pathname === "/billing"}
            onClick={() => {
              navigate("/billing");
              if (opened) toggle();
            }}
            variant="subtle"
            mb={4}
          />

          {/* Reports — permission-gated */}
          {(userPermissions.has("billing.read") ||
            Array.from(userPermissions).some((p) =>
              p.startsWith("reports.")
            )) && (
            <NavLink
              label={desktopCollapsed ? undefined : "Reports"}
              title="Reports"
              leftSection={<IconChartBar size="1.2rem" stroke={1.5} />}
              active={location.pathname.startsWith("/reports")}
              onClick={() => {
                navigate("/reports");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* Case Locks — permission-gated, all tasks */}
          {userPermissions.has("case_locks.force_release") && (
            <NavLink
              label={desktopCollapsed ? undefined : "Case Locks"}
              title="Case Locks"
              leftSection={<IconLock size="1.2rem" stroke={1.5} />}
              active={location.pathname === "/case-locks"}
              onClick={() => {
                navigate("/case-locks");
                if (opened) toggle();
              }}
              variant="subtle"
              mb={4}
            />
          )}

          {/* My Observations — every authenticated user can see their own */}
          <NavLink
            label={desktopCollapsed ? undefined : "My Observations"}
            title="My Observations"
            leftSection={<IconEye size="1.2rem" stroke={1.5} />}
            active={location.pathname.startsWith("/my/observations")}
            onClick={() => {
              navigate("/my/observations");
              if (opened) toggle();
            }}
            variant="subtle"
            mb={4}
          />

          {/* Administration — admin task only */}
          {!isAdminSelected && canManageLegacyCases(profile) && (
            <NavLink
              label={desktopCollapsed ? undefined : "Legacy Cases"}
              title="Legacy Cases"
              leftSection={<IconHistory size={20} />}
              active={location.pathname.startsWith("/admin/legacy-cases")}
              onClick={handleLegacyNavigation}
            />
          )}
          {isAdminSelected && (
            <NavLink
              label={desktopCollapsed ? undefined : "Administration"}
              title="Administration"
              leftSection={<IconSettings size="1.2rem" stroke={1.5} />}
              active={isAdminActive}
              onClick={() => {
                if (desktopCollapsed) {
                  navigate("/admin");
                  if (opened) toggle();
                }
              }}
              variant="subtle"
              mb={4}
              childrenOffset={28}
              defaultOpened={!desktopCollapsed && isAdminActive}
            >
              {!desktopCollapsed && (
                <>
                  {/* Admin search input */}
                  <TextInput
                    placeholder="Search admin pages..."
                    leftSection={<IconSearch size={14} />}
                    size="xs"
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                    mb="xs"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {filteredAdminSections.length === 0 &&
                    debouncedAdminSearch && (
                      <Text size="xs" c="dimmed" ta="center" py="xs">
                        No results found
                      </Text>
                    )}

                  {filteredAdminSections.map((section) => {
                    const sectionActive = section.items.some(
                      (item) => location.pathname === item.path
                    );

                    return (
                      <NavLink
                        key={section.id}
                        label={section.label}
                        leftSection={<section.icon size="1rem" stroke={1.5} />}
                        childrenOffset={28}
                        defaultOpened={sectionActive || !!debouncedAdminSearch}
                        variant="subtle"
                      >
                        {section.items.map((item) => (
                          <NavLink
                            key={item.path}
                            label={item.label}
                            active={location.pathname === item.path}
                            onClick={() => {
                              navigate(item.path);
                              if (opened) toggle();
                            }}
                            variant="subtle"
                          />
                        ))}
                      </NavLink>
                    );
                  })}
                </>
              )}
            </NavLink>
          )}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
